@if (!hasPermission) {
  <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
    <mat-icon class="!text-base text-red-400">lock</mat-icon>
    Permissions Restricted
  </div>
} @else {
  <div class="flex flex-col">
    <!-- Header -->
    <div class="mb-3 flex items-center justify-between">
      <h3 class="text-secondary text-sm font-semibold uppercase tracking-wide">{{ title }}</h3>
      @if (!loading && totalApprovals > 0) {
        <span class="rounded-full px-2 py-0.5 text-xs font-semibold" [ngClass]="totalApprovals | countBadge">{{ totalApprovals }}</span>
      }
    </div>

    <!-- Loading -->
    @if (loading) {
      <div class="flex justify-center py-8"><mat-spinner diameter="32" color="primary" /></div>
    }

    <!-- Error -->
    @if (error && !loading) {
      <div class="py-4 text-center text-sm text-red-500">{{ error }}</div>
    }

    <!-- Content -->
    @if (!loading && !error) {
      @if (approvals.length === 0) {
        <div class="text-secondary rounded-lg border border-dashed py-8 text-center text-sm">
          <mat-icon class="mb-1 !text-3xl text-gray-300">done_all</mat-icon>
          <div>No pending approvals</div>
        </div>
      }

      <div class="divide-y divide-gray-100">
        @for (item of approvals; track item.type + item.id) {
          @if (item.type === 'bill') {
            <!-- Bill item — compact, consistent with widget design -->
            <div class="flex cursor-pointer items-start gap-2.5 py-2.5 transition-colors hover:bg-gray-50" (click)="takeAction(item)">
              <!-- Icon badge -->
              <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
                <mat-icon class="!text-base text-blue-500">receipt_long</mat-icon>
              </div>
              <!-- Content -->
              <div class="min-w-0 flex-1">
                <div class="line-clamp-1 text-xs font-semibold leading-tight text-gray-800">
                  {{ item.reference ?? 'Bill #' + item.id }}
                  @if (item.vendor_name) {
                    <span class="font-normal text-gray-400"> · {{ item.vendor_name }}</span>
                  }
                </div>
                <div class="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs text-gray-400">
                  @if (item.community) {
                    <span class="font-medium text-gray-500">{{ item.community }}</span>
                  }
                  @if (item.bill_date) {
                    <span class="text-gray-200">·</span>
                    <span>{{ item.bill_date | date: 'MMM d' }}</span>
                  }
                  @if (item.due_date) {
                    <span class="text-gray-200">·</span>
                    <span>Due {{ item.due_date | date: 'MMM d' }}</span>
                  }
                </div>
                @if (item.vendor_license_expiry) {
                  <div class="mt-0.5 flex items-center gap-0.5 text-[10px]" [ngClass]="getLicenseExpiryClass(item.vendor_license_expiry)">
                    <mat-icon class="!h-3 !w-3 !text-[10px] !leading-none">event_available</mat-icon>
                    License exp. {{ item.vendor_license_expiry | date: 'MMM d, y' }}
                  </div>
                }
              </div>
              <!-- Right: amount + status pill -->
              <div class="flex flex-shrink-0 flex-col items-end gap-1">
                <span class="text-xs font-bold text-gray-800" [innerHTML]="item.amount | amount"></span>
                <span
                  class="rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none"
                  [class.bg-blue-100]="item.status?.toLowerCase() === 'sent for review'"
                  [class.text-blue-700]="item.status?.toLowerCase() === 'sent for review'"
                  [class.bg-orange-100]="item.status?.toLowerCase() === 'sent for approval'"
                  [class.text-orange-700]="item.status?.toLowerCase() === 'sent for approval'"
                  [class.bg-purple-100]="item.status?.toLowerCase() === 'sent for audit approval'"
                  [class.text-purple-700]="item.status?.toLowerCase() === 'sent for audit approval'"
                  [class.bg-gray-100]="item.status?.toLowerCase() === 'sent back'"
                  [class.text-gray-600]="item.status?.toLowerCase() === 'sent back'"
                >
                  {{ item.status | titlecase }}
                </span>
              </div>
            </div>
          } @else {
            <!-- Non-bill item — compact row -->
            <div class="flex items-center gap-2.5 py-2.5">
              <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-orange-50">
                <mat-icon class="!text-base text-orange-600">{{ getTypeIcon(item.type) }}</mat-icon>
              </div>
              <div class="min-w-0 flex-1">
                <div class="text-default line-clamp-1 text-xs font-semibold leading-tight">{{ item.title }}</div>
                <div class="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-gray-400">
                  <span>{{ getTypeLabel(item.type) }}</span>
                  @if (item.community) {
                    <span class="text-gray-200">·</span>
                    <span class="truncate">{{ item.community }}</span>
                  }
                  @if (item.amount) {
                    <span class="text-gray-200">·</span>
                    <span class="font-semibold text-blue-600"><span class="currency normal-case">&#xe001;</span> {{ item.amount | number: '1.0-0' }}</span>
                  }
                </div>
              </div>
              <button
                class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-primary/10 hover:text-primary"
                matTooltip="Take action"
                (click)="takeAction(item)"
              >
                <mat-icon class="!text-base !leading-none">open_in_new</mat-icon>
              </button>
            </div>
          }
        }
      </div>
    }
  </div>
}
